// oxlint-disable typescript/no-explicit-any
import React, { useState } from 'react';

import { useCSVReader } from 'react-papaparse';

import UploadFileIcon from '@mui/icons-material/UploadFile';

import DeleteIcon from '@mui/icons-material/Delete';

import { observer } from 'mobx-react';

import Box from '@mui/material/Box';

import { Button } from 'components/Button';

import { UploadIcon } from 'components/Icons/UploadIcon';

import { useStore } from 'utils/hooks/store';

import { colors } from 'theme/colors';

import { UploadUserType } from 'utils/api/types';
import { createCsvHeaderTransform } from 'utils/csv/csvHeaderTransform';
import { unwrapExcelSafeCell } from 'utils/csv/excelSafeCell';
import { ImportVocabulary } from 'utils/csv/importVocabulary';

import {
  FileContainer,
  FileName,
  InfoMessage,
  RemoveButton,
  SecondaryMessage,
  UploadContainer,
  BaseInfoText,
} from './styles';

import DownloadButton from '../DownloadButton';

const ClickAndDragUpload: React.FunctionComponent<{ vocabulary: ImportVocabulary }> = ({ vocabulary }) => {
  const { CSVReader } = useCSVReader();
  const [zoneHover, setZoneHover] = useState(false);

  const { notification, userList } = useStore();

  return (
    <CSVReader
      multiple={false}
      config={{
        header: true,
        transformHeader: createCsvHeaderTransform(vocabulary),
        transform: unwrapExcelSafeCell,
      }}
      maxSize={1000000}
      onUploadRejected={(file: File[]) => {
        if (file[0]) {
          // @ts-expect-error
          notification.enqueueErrorSnackbar(file[0].errors[0].message);
        } else {
          notification.enqueueErrorSnackbar('Error uploading file. Please try again.');
        }
        setZoneHover(false);
      }}
      onUploadAccepted={(results: { data: UploadUserType[]; meta?: { fields?: string[] } }) => {
        userList.loadUsersFromCSV(results.data, { vocabulary, fields: results.meta?.fields ?? [] });
        setZoneHover(false);
      }}
      onDragOver={(event: DragEvent) => {
        event.preventDefault();
        setZoneHover(true);
      }}
      onDragLeave={(event: DragEvent) => {
        event.preventDefault();
        setZoneHover(false);
      }}
    >
      {({ getRootProps, acceptedFile, ProgressBar, getRemoveFileProps }: any) => (
        <>
          <BaseInfoText>
            <DownloadButton vocabulary={vocabulary} />, fill in the user information without changing the column names,
            and upload the file below.
          </BaseInfoText>
          <UploadContainer {...getRootProps()} $dragActive={zoneHover}>
            <UploadIcon />
            <InfoMessage>Choose a file or drag & drop it here</InfoMessage>
            <SecondaryMessage>CSV format (comma delimited)</SecondaryMessage>
            <Button variant="outlined" size="large" sx={{ marginTop: '16px' }} disabled={acceptedFile !== null}>
              Browse Files
            </Button>
          </UploadContainer>

          {acceptedFile && userList.uploadErrors.length === 0 && (
            <FileContainer>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <UploadFileIcon
                    style={{
                      marginRight: '16px',
                      width: '32px',
                      height: '32px',
                      color: colors.text.secondary,
                    }}
                  />
                  <FileName>{acceptedFile.name}</FileName>
                </Box>
                <RemoveButton
                  {...getRemoveFileProps()}
                  onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                    getRemoveFileProps().onClick(event);
                    userList.clearUsersToUpload();
                  }}
                >
                  <DeleteIcon sx={{ color: 'text.secondary' }} />
                </RemoveButton>
              </Box>

              <ProgressBar style={{ marginTop: '16px' }} />
            </FileContainer>
          )}
        </>
      )}
    </CSVReader>
  );
};

export default observer(ClickAndDragUpload);
